import { useCallback } from 'react';
import type { Message, MessageV2 } from '@/hooks/useCommunication';
import { useCommunication } from '@/hooks/useCommunication';
import type { ISerialPort } from '@/hooks/useSerialPort';
import { readReader, useSerialPort } from '@/hooks/useSerialPort';
import { BwsBuscadorParser } from '@/lib/protocol/parser/bws-buscador';
import { sleep } from '@/lib/utils';
import type { Device } from '@/types/production/device';

namespace Namespace {
  export interface Equipment {
    firmware: string;
    serial: string;
    imei?: string | undefined;
    iccid?: string | undefined;
    mac?: string | undefined;
    lora_keys?: Partial<Device['Equipment']['lora_keys']>;
  }
  export interface Detected {
    port: ISerialPort;
    equipment: Equipment;
  }
}

const readResponse = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  command: string,
  timeout: number = 500,
  check?: string,
  isComplete?: (chunk: string) => boolean,
): Promise<string | undefined> => {
  const decoder = new TextDecoder();
  let buffer = '';

  const base = command.replace('\r\n', '');
  const cmp = check ? check : base.includes('=') ? base : `${base}=`;

  // Uma linha fechada por \r\n já chegou inteira; só o trecho pendente precisa
  // do critério extra de completude.
  const matches = (chunk: string) => chunk.includes(cmp);
  const pendingMatches = (chunk: string) =>
    matches(chunk) && (isComplete ? isComplete(chunk) : true);

  const timeoutPromise = new Promise<undefined>((resolve) =>
    setTimeout(() => resolve(undefined), timeout),
  );

  const readPromise = (async () => {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value);

      console.info('[RAW DATA]', buffer);

      const lines = buffer.split('\r\n');
      // A última parte pode ser uma linha incompleta — continua no buffer para
      // ser completada pela próxima leitura.
      const pending = lines.pop() || '';
      buffer = pending;

      for (const line of lines) {
        if (line.length > 0 && matches(line)) {
          return line;
        }
      }

      // O eco do BLEMACW ("MACW=...") chega no meio do dump periódico do
      // firmware (Count Sec / Modem ja ON) e SEM \r\n final — fica preso no
      // trecho pendente e o comando expirava por timeout mesmo tendo sido
      // gravado. Por isso o pendente também é inspecionado, sem ser consumido.
      //
      // O eco ainda pode vir partido entre duas leituras ("...MACW=4" e depois
      // "CFAC90B0000"): sem o critério de completude a leitura encerrava em cima
      // do valor pela metade e a gravação era reportada como falha. O pendente
      // continua acumulando até o valor chegar inteiro.
      if (pending.length > 0 && pendingMatches(pending)) {
        return pending;
      }
    }
    return undefined;
  })();

  return Promise.race([readPromise, timeoutPromise]);
};

/**
 * Driver do BWS Buscador.
 *
 * O Buscador reaproveita o protocolo do NB2 para serial e IMEI (WINS/RINS e
 * WIMEI/RIMEI) e acrescenta a gravação do MAC address BLE via BLEMACW.
 *
 * O MAC vem SEMPRE do cadastro (engineer.serial-relations), nunca da etiqueta
 * lida pelo operador. Quem busca o MAC e bloqueia a gravação quando ele não
 * existe é o useIdentification — este driver apenas recebe o valor já validado.
 *
 * Por ora o Buscador só participa do identificador: não há perfis de
 * configuração, autotest nem atualização de firmware para este modelo.
 */
export const useBWSBuscador = () => {
  const { ports, openPort, closePort, getReader, writeToPort, requestPort } = useSerialPort({});

  const { sendMultipleMessages, send } = useCommunication<ISerialPort>({
    openTransport: async (transport) => {
      await openPort(
        transport,
        {
          baudRate: 115200,
          stopBits: 2,
          bufferSize: 1024,
        },
        150,
      );
    },
    closeTransport: closePort,
    sendMessage: async (
      port,
      msg: Message<
        string,
        { check?: string; delay_before?: number; is_complete?: (chunk: string) => boolean }
      >,
    ) => {
      const reader = await getReader(port);
      if (!reader) throw new Error('Reader não disponível');
      const { command, timeout, delay_before, check, is_complete } = msg;
      if (delay_before) await sleep(delay_before);
      console.info('-------------------------');
      console.info('[MESSAGE SENT]', command);
      await writeToPort(port, command);
      const response = await readResponse(reader, command, timeout, check, is_complete);
      console.info('[RESPONSE MATCHED]', response);

      await reader.cancel();
      reader.releaseLock();
      return response;
    },
    sendMessageV2: async (
      port,
      msg: MessageV2<{
        delay_before?: number;
        delay_after?: number;
        check?: string;
      }>,
    ) => {
      const { command, delay_before, delay_after } = msg;
      if (delay_before) await sleep(delay_before);
      await writeToPort(port, command);
      console.log('[COMMAND]', command);
      if (delay_after) await sleep(delay_after);
    },
    options: {
      delayBetweenMessages: 400,
      maxRetriesPerMessage: 3,
      maxOverallRetries: 2,
    },
  });

  const handleDetection = useCallback(
    async (ports: ISerialPort[]) => {
      const messages = [
        { command: 'RIMEI\r\n' },
        { command: 'ICCID\r\n' },
        { command: 'RINS\r\n' },
        { command: 'RFW\r\n' },
      ] as const;

      return await Promise.all(
        ports.map(async (port) => {
          try {
            const { pos } = await send({
              transport: port,
              messages,
              preChunk: async (transport) => {
                await getReader(transport);
              },
              posChunk: async (transport) => {
                const reader = await getReader(transport);
                const result = await readReader(reader, 10000);
                return result;
              },
            });

            const detection = pos
              ? BwsBuscadorParser.parser(pos, 'r_imei', 'r_serial', 'r_firmware', 'r_iccid')
              : undefined;

            return {
              port,
              response: {
                imei: detection?.r_imei,
                serial: detection?.r_serial,
                firmware: detection?.r_firmware,
                iccid: detection?.r_iccid,
              },
            };
          } catch (error) {
            // Porta em uso ou device desconectado: silencioso, pois a detecção
            // roda em loop a cada 5s e o device reaparece no próximo ciclo.
            if (
              (error as Error).name === 'NetworkError' ||
              (error as Error).message?.includes('Failed to open serial port')
            ) {
              return { port };
            }
            console.error('[ERROR] handleDetection use-bws-buscador', error);
            return { port };
          }
        }),
      );
    },
    [getReader, send],
  );

  /**
   * Grava serial, IMEI e MAC no dispositivo.
   *
   * @param id serial a ser gravado (extraído da etiqueta pelo operador)
   * @param imeiToWrite IMEI do cadastro
   * @param macToWrite MAC do cadastro — obrigatório; o bloqueio quando ausente
   *                   acontece antes, no useIdentification
   */
  const handleIdentification = useCallback(
    async (detected: Namespace.Detected, id: string, imeiToWrite?: string, macToWrite?: string) => {
      const { port, equipment } = detected;

      try {
        const messages: any[] = [];

        if (imeiToWrite) {
          console.log('[BUSCADOR] Gravando IMEI:', imeiToWrite);
          messages.push({
            key: 'write_imei',
            command: `WIMEI=${imeiToWrite}\r\n`,
            check: 'WIMEI=',
            delay_after: 500,
          });
        }

        messages.push({
          key: 'write_serial',
          command: `WINS=${id}\r\n`,
          check: 'WINS=',
          delay_after: 500,
        });

        if (macToWrite) {
          console.log('[BUSCADOR] Gravando MAC:', macToWrite);
          messages.push({
            key: 'write_mac',
            command: `BLEMACW=${macToWrite}\r\n`,
            // O firmware responde "MACW=<mac>", mas esse eco NÃO é confiável: ele
            // divide a serial com o log contínuo de diagnóstico do firmware e com
            // frequência não sai dentro da janela, mesmo com a gravação tendo sido
            // aceita pelo device. Por isso ele é apenas registrado (log e relatório)
            // e não entra no cálculo do status — ver comentário em `status` abaixo.
            check: 'MACW=',
            // Quando o eco chega, pode vir partido entre duas leituras; só encerra
            // quando os 12 hex já tiverem chegado, para não registrar valor parcial.
            is_complete: BwsBuscadorParser.hasCompleteMac,
            timeout: 4000,
            delay_after: 500,
          });
        }

        messages.push(
          {
            key: 'r_serial',
            command: `RINS\r\n`,
            check: 'RINS=',
            // Após gravar, o device demora a responder o RINS; o timeout default
            // (500ms) fecharia a janela antes da resposta chegar.
            timeout: 5000,
            delay_before: 3000,
          },
          {
            key: 'r_imei',
            command: `RIMEI\r\n`,
            check: 'RIMEI=',
            timeout: 5000,
          },
        );

        const init_time = Date.now();
        const response = await sendMultipleMessages({
          transport: port,
          messages,
        });

        const [imei, serial, mac] = [
          BwsBuscadorParser.imei(response.r_imei),
          BwsBuscadorParser.serial(response.r_serial),
          macToWrite ? BwsBuscadorParser.mac(response.write_mac) : undefined,
        ];

        // O status considera apenas serial e IMEI, que são RELIDOS do dispositivo
        // (RINS/RIMEI) e portanto confirmam de fato a gravação.
        //
        // O MAC fica de fora: não existe comando de leitura do MAC, e o eco
        // "MACW=" mostrou-se não confiável — o firmware emite log de diagnóstico
        // continuamente e o eco frequentemente não sai dentro da janela, ainda que
        // a gravação tenha sido aceita. Exigi-lo reprovava equipamentos gravados
        // corretamente.
        //
        // ATENÇÃO: com isso não resta nenhuma verificação da gravação do MAC. Uma
        // falha real de gravação passa como sucesso. O que ainda reprova é o
        // comando BLEMACW não conseguir ser ENVIADO — nesse caso sendMultipleMessages
        // lança e caímos no catch abaixo. O eco continua sendo lido e devolvido em
        // `messages`/`equipment_after` para auditoria.
        const serialMatches = serial === id;
        const imeiMatches = imeiToWrite ? imei === imeiToWrite : true;
        const status = serialMatches && imeiMatches;

        console.info(
          `[BUSCADOR] serial ${serialMatches ? 'OK' : `MISMATCH (esperado ${id}, lido ${serial ?? '-'})`} | ` +
            `imei ${imeiMatches ? 'OK' : `MISMATCH (esperado ${imeiToWrite}, lido ${imei ?? '-'})`} | ` +
            `mac ${mac ? `eco ${mac}` : 'sem eco'} (não afeta o status)`,
        );

        const end_time = Date.now();
        return {
          port,
          init_time,
          end_time,
          status,
          messages: messages.map(({ key, command }) => ({
            key,
            request: command,
            response: response[key],
          })),
          equipment_before: {
            serial: equipment.serial,
            imei: equipment.imei,
            mac: equipment.mac,
          },
          equipment_after: {
            serial,
            imei,
            mac,
          },
        };
      } catch (error) {
        const message = `[(${new Date().toLocaleString()}) ERROR IN BUSCADOR IDENTIFICATION]`;
        console.error(message, error);
        throw new Error(message);
      }
    },
    [sendMultipleMessages],
  );

  const isIdentified = (input?: {
    imei?: string;
    iccid?: string;
    serial?: string;
    firmware?: string;
  }): 'fully_identified' | 'partially_identified' | 'not_identified' => {
    if (!input) return 'not_identified';
    const { serial, imei, iccid, firmware } = input;
    const identified = [serial, imei, iccid, firmware];
    if (identified.every((e) => e && e.length > 0)) {
      return 'fully_identified';
    } else if (identified.some((e) => e && e.length > 0)) {
      return 'partially_identified';
    } else {
      return 'not_identified';
    }
  };

  // O Buscador participa APENAS do identificador. As demais ferramentas de
  // produção (perfis de configuração, autotest e atualização de firmware) ainda
  // não têm especificação para este modelo.
  //
  // Os métodos abaixo existem para manter a mesma superfície dos outros drivers
  // — o useTechnology devolve uma união e os hooks dessas ferramentas
  // desestruturam esses nomes — e falham de forma explícita se forem chamados,
  // em vez de enviar comandos de outro modelo ao dispositivo.
  const unsupported = (feature: string) => async (): Promise<never> => {
    const message = `[BUSCADOR] ${feature} não é suportado pelo BWS Buscador.`;
    console.error(message);
    throw new Error(message);
  };

  const handleGetConfig = useCallback(unsupported('Leitura de configuração'), []);
  const handleConfiguration = useCallback(unsupported('Configuração'), []);
  const handleAutoTest = useCallback(unsupported('Autoteste'), []);
  const handleFirmwareUpdate = useCallback(unsupported('Atualização de firmware'), []);

  return {
    isIdentified,
    ports,
    handleIdentification,
    handleDetection,
    requestPort,
    handleGetConfig,
    handleConfiguration,
    handleAutoTest,
    handleFirmwareUpdate,
  };
};
